import { Badge, BottomSheet, Button, Card, CardHeader, Drawer, EmptyState, Skeleton, Table, bpToPercent, parsePercentInput, useMediaQuery, useToast } from "@helmr/ui";
import { diffLevers, leversFromLegacy, type BranchId, type LeverId, type RolePermissions } from "@helmr/contracts";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { isApiError } from "../../api/client";
import { heldLevers } from "../../api/me";
import { createRole, deleteRole, getLeverTree, getRole, listPresets, listRoles, patchRole, type LeverNode, type LeverTree, type RoleRecord } from "../../api/roles";
import { PHONE_QUERY } from "../../app/mobile/query";
import { Failure, Field, Load, mapError, Page, TableSkeleton, useDirtyGuard, useScope, type Mapped } from "./kit";
import { RoleTree } from "./RoleTree";
import { diffText, labelsOf } from "./rolesTree";
import rs from "./roles.module.css";
import { useBranchOptions } from "./SectionPages";
import s from "./settings.module.css";
import { section } from "./sections";
import { t } from "./strings";

const NOT_HELD = "You don't have this";

/**
 * What the signed-in person holds. Levers decide what they may grant or remove (the API refuses the rest with `privilege_escalation`);
 * the legacy grants still carry scope and the discount cap they may hand out. Without `/me` nothing is known, so nothing is blocked here and the server decides.
 */
function useActor() {
  const { ctx } = useScope();
  const g = ctx.me?.grants;
  const owner = ctx.isOwner || !g || ("owner" in g && g.owner === true);
  const grants = g && !owner ? (g as RolePermissions) : null;
  const held = ctx.status === "unavailable" ? "all" : heldLevers(ctx.me);
  return {
    owner,
    has: (id: LeverId) => held === "all" || held.has(id),
    maxCapBp: owner ? 10000 : (grants?.discountCapBp ?? 0),
    holdsAllClients: owner || grants?.scope.clients === "all",
    holdsAllBranches: owner || grants?.scope.branches === "all",
    branchIds: owner || grants?.scope.branches === "all" ? null : ((grants?.scope.branches as string[]) ?? []),
    ownRoleId: ctx.me?.staff.roleId ?? null,
  };
}

/** A bottom sheet on a phone (D56), a right-hand drawer elsewhere. */
function Sheet({ open, onClose, title, description, footer, children }: { open: boolean; onClose: () => void; title: string; description?: string; footer: ReactNode; children: ReactNode }) {
  const phone = useMediaQuery(PHONE_QUERY);
  const change = (o: boolean) => { if (!o) onClose(); };
  return phone
    ? <BottomSheet open={open} onOpenChange={change} title={title} description={description} footer={footer}>{children}</BottomSheet>
    : <Drawer open={open} onOpenChange={change} title={title} description={description} footer={footer}>{children}</Drawer>;
}

export function TreeSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div className={rs.tree} aria-busy="true" aria-label="Loading levers">
      <Skeleton height={30} />
      <div className={rs.groups}>
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className={rs.ghead}><div className={rs.gtext}><Skeleton width={120 + (i % 3) * 30} height={13} /><Skeleton width={60} height={11} /></div><Skeleton width={44} height={26} /></div>
        ))}
      </div>
    </div>
  );
}

/* ---------- list ---------- */

export function RolesPage() {
  const sc = useScope();
  const roles = useQuery({ queryKey: sc.key("roles"), queryFn: () => listRoles(sc.client), retry: false });
  const presets = useQuery({ queryKey: sc.key("role-presets"), queryFn: () => listPresets(sc.client), retry: false });
  const canAdd = sc.ctx.has("roles.roles_add");
  return (
    <Page title={t.sections.roles.title} description={t.sections.roles.body} allow={section("roles")}
      actions={canAdd ? <Button variant="primary" as={Link} to="/settings/roles/new">New role</Button> : undefined}>
      <Card aria-label="Roles">
        <CardHeader title="Roles" subtitle="Every staff member has one role. The Owner role cannot be changed." />
        <Load query={roles} what="roles" skeleton={<TableSkeleton rows={5} />}>
          {(list) => list.length === 0
            ? <EmptyState compact icon="badge" title="No roles yet" description="Start from a preset." action={canAdd ? <Button as={Link} to="/settings/roles/new">New role</Button> : undefined} />
            : <Table label="Roles" rows={list} rowKey={(r) => r.id}
              columns={[
                { key: "n", header: "Role", cell: (r) => <Link to={`/settings/roles/${r.id}`}><span className={rs.rowName} title={r.name}>{r.name}</span></Link> },
                { key: "p", header: "Started from", cell: (r) => r.presetFrom ?? "—" },
                { key: "f", header: "Access", cell: (r) => (r.isOwner ? <Badge tone="info">Everything</Badge> : r.grantsInvalid ? <Badge tone="crit">Needs repair</Badge> : `${r.levers.length} levers`) },
              ]} />}
        </Load>
      </Card>
      {canAdd && presets.data?.length ? (
        <Card aria-label="Presets">
          <CardHeader title="Start from a preset" subtitle="A preset is copied into an ordinary role you can change." />
          <div className={s.pad}><div className={s.acts}>{presets.data.map((p) => <Button key={p.key} as={Link} to={`/settings/roles/new?preset=${p.key}`}>{p.name}</Button>)}</div></div>
        </Card>
      ) : null}
    </Page>
  );
}

/* ---------- editor ---------- */

interface Draft { name: string; preset: string | null; levers: ReadonlySet<LeverId>; scope: RolePermissions["scope"]; cap: string }

const draftOf = (r: RoleRecord): Draft => ({ name: r.name, preset: r.presetFrom ?? null, levers: new Set(r.levers), scope: structuredClone(r.scope), cap: bpToPercent(r.discountCapBp) });
const EMPTY: Draft = { name: "", preset: null, levers: new Set(), scope: { clients: "own", branches: "all" }, cap: "0" };
const sorted = (x: Iterable<LeverId>) => [...x].sort();

export function RolePage() {
  const { id = "" } = useParams();
  const isNew = id === "new";
  const [params] = useSearchParams();
  const sc = useScope();
  const [conflict, setConflict] = useState(false);
  const role = useQuery({ queryKey: sc.key(`role:${id}`), queryFn: () => getRole(sc.client, id), enabled: !isNew, retry: false });
  const tree = useQuery({ queryKey: sc.key("role-levers"), queryFn: () => getLeverTree(sc.client), retry: false });
  const presets = useQuery({ queryKey: sc.key("role-presets"), queryFn: () => listPresets(sc.client), retry: false, enabled: isNew });
  // What this person holds decides what they may grant, so the editor waits for /me too (a preset is trimmed to it on first render).
  const pending = sc.ctx.status === "loading" || (isNew ? presets.isPending || tree.isPending : role.isPending || tree.isPending);
  const error = (isNew ? presets.error : role.error) ?? tree.error;
  return (
    <Page title={isNew ? "New role" : (role.data?.name ?? "Role")} description={t.sections.roles.body} allow={section("roles")}>
      {/* Not a Card: its overflow:hidden would stop the save bar from sticking to the bottom of the screen. */}
      <section className={rs.panel} aria-label="Role editor">
        <h2 className={rs.panelHead}>{isNew ? "New role" : "Role"}</h2>
        {pending ? <TreeSkeleton /> : error ? <Failure error={error} what="the role" onRetry={() => { void tree.refetch(); void role.refetch(); void presets.refetch(); }} />
          : <RoleEditor key={`${id}:${role.data?.version ?? 0}`} role={role.data ?? null} tree={tree.data!} presets={presets.data ?? []} startPreset={params.get("preset")}
            conflict={conflict} onConflict={() => { setConflict(true); void role.refetch(); }} onClearConflict={() => setConflict(false)} />}
      </section>
    </Page>
  );
}

type PresetOption = { key: string; name: string; grants: RolePermissions };

function RoleEditor({ role, tree, presets, startPreset, conflict, onConflict, onClearConflict }: {
  role: RoleRecord | null; tree: LeverTree; presets: PresetOption[]; startPreset: string | null; conflict: boolean; onConflict: () => void; onClearConflict: () => void;
}) {
  const sc = useScope();
  const nav = useNavigate();
  const toast = useToast();
  const actor = useActor();
  const branches = useBranchOptions();
  const isNew = role === null;
  const canGrant = (id: LeverId) => actor.has(id);
  const fromPreset = (key: string | null): Draft => {
    const p = presets.find((x) => x.key === key);
    if (!p) return EMPTY;
    // A preset is only a starting set; what you cannot grant yourself is left out rather than failing the save.
    const all = leversFromLegacy(p.grants);
    const mine = all.filter(canGrant);
    return { name: p.name, preset: p.key, levers: new Set(mine), scope: structuredClone(p.grants.scope), cap: bpToPercent(Math.min(p.grants.discountCapBp, actor.maxCapBp)) };
  };
  /** How many of a preset's levers were left out because you don't hold them (0 for no preset). */
  const skippedOf = (key: string | null): number => {
    const p = presets.find((x) => x.key === key);
    return p ? leversFromLegacy(p.grants).filter((id) => !canGrant(id)).length : 0;
  };
  const initial = useMemo(() => (role ? draftOf(role) : fromPreset(startPreset)), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [draft, setDraft] = useState<Draft>(initial);
  const [saved, setSaved] = useState(initial);
  const initialSkipped = useMemo(() => (isNew ? skippedOf(startPreset) : 0), []); // eslint-disable-line react-hooks/exhaustive-deps
  // Follows the preset picked in the editor, so the note never describes a preset that is no longer chosen.
  const [skipped, setSkipped] = useState(initialSkipped);
  const [err, setErr] = useState<Mapped>({ fields: {}, form: null });
  const [confirm, setConfirm] = useState(false);
  const [del, setDel] = useState(false);
  const [delErr, setDelErr] = useState<string | null>(null);

  const ownerRole = role?.isOwner === true;
  const own = role !== null && role.id === actor.ownRoleId && !actor.owner;
  const readOnly = ownerRole || own || !(isNew ? actor.has("roles.roles_add") : actor.has("roles.roles_edit"));
  // The owner holds every lever implicitly; show them all on even if the list came back empty.
  const shown = ownerRole && draft.levers.size === 0 ? new Set(tree.groups.flatMap((g) => g.levers.map((l) => l.id))) : draft.levers;

  const capOn = draft.levers.has("billing.discount");
  const capParsed = parsePercentInput(draft.cap);
  const capError = !capOn ? null : !capParsed.ok ? "Enter a percentage such as 10" : capParsed.bp > actor.maxCapBp ? `Cannot be above your own cap of ${bpToPercent(actor.maxCapBp)}%` : null;
  const capBp = role ? (capOn && capParsed.ok ? capParsed.bp : role.discountCapBp) : capOn && capParsed.ok ? capParsed.bp : 0;

  const diff = diffLevers(saved.levers, draft.levers);
  const others = [
    draft.name.trim() !== saved.name.trim() ? "name" : null,
    JSON.stringify(draft.scope) !== JSON.stringify(saved.scope) ? "who and where" : null,
    capOn && draft.cap !== saved.cap ? "discount cap" : null,
  ].filter((x): x is string => x !== null);
  const dirty = diff.added.length + diff.removed.length > 0 || others.length > 0;
  const { node: guard, release } = useDirtyGuard(dirty);

  const save = useMutation({
    mutationFn: () => {
      const body = { name: draft.name.trim(), levers: sorted(draft.levers), scope: draft.scope, discountCapBp: capBp };
      return isNew ? createRole(sc.client, { ...body, ...(draft.preset ? { preset: draft.preset } : {}) }) : patchRole(sc.client, role.id, { ...body, version: role.version });
    },
    onSuccess: async (r) => {
      toast.show(t.saved);
      setConfirm(false);
      await sc.qc.invalidateQueries({ queryKey: sc.key("roles") });
      onClearConflict();
      release();
      if (isNew) nav(`/settings/roles/${r.id}`, { replace: true });
      else sc.qc.setQueryData(sc.key(`role:${r.id}`), r); // a new version remounts the editor on what the server holds
    },
    onError: (e) => {
      setConfirm(false);
      if (isApiError(e) && e.status === 409) { onConflict(); return; }
      setErr(mapError(e, ["name", "levers", "scope", "discountCapBp"]));
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteRole(sc.client, role!.id),
    onSuccess: async () => { await sc.qc.invalidateQueries({ queryKey: sc.key("roles") }); release(); toast.show(`Deleted ${role!.name}`); nav("/settings/roles"); },
    onError: (e) => setDelErr(mapError(e, []).form),
  });

  useEffect(() => { setErr((e) => (e.form || Object.keys(e.fields).length ? { fields: {}, form: null } : e)); }, [draft]);

  const submit = () => {
    if (capError) { setErr({ fields: {}, form: t.validation }); return; }
    if (isNew) save.mutate(); else setConfirm(true);
  };

  const discountCap = (l: LeverNode, on: boolean) => l.id === "billing.discount" && on ? (
    <div className={rs.cap}>
      <label htmlFor="role-cap">Up to</label>
      <input id="role-cap" inputMode="decimal" value={draft.cap} disabled={readOnly || !actor.has("billing.discount")} aria-invalid={!!capError} aria-describedby={capError ? "role-cap-err" : undefined}
        onChange={(e) => setDraft({ ...draft, cap: e.target.value })} />
      <span>% off a bill (your own cap is {bpToPercent(actor.maxCapBp)}%)</span>
      {capError ? <span id="role-cap-err" className={rs.capErr} role="alert">{capError}</span> : null}
    </div>
  ) : null;

  const lines = (ids: readonly LeverId[]) => labelsOf(tree, ids);
  const noChange = diff.added.length + diff.removed.length === 0;

  return (
    <form className={s.form} onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate>
      {guard}
      {conflict ? (
        <div className={rs.banner} role="alert"><span>Someone else changed this role. Your changes were not saved; this is the latest version.</span><button type="button" onClick={onClearConflict}>Dismiss</button></div>
      ) : null}
      {ownerRole ? <div className={s.note}>The owner can do everything.</div> : own ? <div className={s.note}>Ask the owner to change your role.</div>
        : readOnly ? <div className={s.note}>You can view this role, not change it.</div> : null}
      {role?.grantsInvalid ? <div className={s.formErr} role="alert">This role&apos;s stored grants are invalid and show as empty. Saving replaces them.</div> : null}
      {skipped > 0 ? <div className={s.note}>{skipped} {skipped === 1 ? "lever" : "levers"} from this preset left out because you don&apos;t hold {skipped === 1 ? "it" : "them"} yourself.</div> : null}

      <div className={s.fields}>
        <Field label="Role name" error={err.fields.name} htmlFor="role-name">
          <input id="role-name" className={s.input} value={draft.name} disabled={readOnly} maxLength={80} aria-invalid={!!err.fields.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </Field>
        {isNew ? (
          <Field label="Start from a preset" htmlFor="role-preset" hint="Replaces the levers below.">
            <select id="role-preset" className={s.select} value={draft.preset ?? ""} onChange={(e) => { const d = e.target.value ? fromPreset(e.target.value) : { ...EMPTY, name: draft.name }; setDraft(d); setSkipped(skippedOf(e.target.value || null)); }}>
              <option value="">Blank: nothing assigned</option>{presets.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
            </select>
          </Field>
        ) : null}
        <Field label="Clients" htmlFor="scope-clients">
          <select id="scope-clients" className={s.select} disabled={readOnly} value={draft.scope.clients} onChange={(e) => setDraft({ ...draft, scope: { ...draft.scope, clients: e.target.value as "all" | "own" } })}>
            <option value="own">Only their own clients</option>
            <option value="all" disabled={!actor.holdsAllClients} title={!actor.holdsAllClients ? NOT_HELD : undefined}>Every client</option>
          </select>
        </Field>
        <Field label="Branches" htmlFor="scope-branches">
          <select id="scope-branches" className={s.select} disabled={readOnly} value={draft.scope.branches === "all" ? "all" : "chosen"}
            onChange={(e) => setDraft({ ...draft, scope: { ...draft.scope, branches: e.target.value === "all" ? "all" : ([] as BranchId[]) } })}>
            <option value="all" disabled={!actor.holdsAllBranches} title={!actor.holdsAllBranches ? NOT_HELD : undefined}>Every branch</option>
            <option value="chosen">Chosen branches</option>
          </select>
        </Field>
        {draft.scope.branches !== "all" ? (
          <div className={`${s.field} ${s.full}`}><span className={s.lab}>Which branches</span>
            <div className={s.checks} role="group" aria-label="Chosen branches">
              {branches.map((b) => {
                const list = draft.scope.branches as string[];
                const held = actor.branchIds === null || actor.branchIds.includes(b.id);
                return <label key={b.id} className={s.check} title={!held ? NOT_HELD : undefined}>
                  <input type="checkbox" disabled={readOnly || (!held && !list.includes(b.id))} checked={list.includes(b.id)}
                    onChange={(e) => setDraft({ ...draft, scope: { ...draft.scope, branches: (e.target.checked ? [...list, b.id] : list.filter((x) => x !== b.id)) as BranchId[] } })} /><span title={b.name}>{b.name}</span></label>;
              })}
            </div>
          </div>
        ) : null}
      </div>

      <RoleTree tree={tree} held={shown} onChange={(next) => setDraft({ ...draft, levers: next })} canGrant={canGrant} readOnly={readOnly}
        roleVersion={role ? (role.leverRegistryVersion ?? 1) : tree.version} below={discountCap} />

      {err.form ? <div className={s.formErr} role="alert">{err.form}</div> : null}
      <div className={rs.saveBar}>
        <span className={rs.changeText} aria-live="polite">
          {readOnly ? "You can view this, not change it." : !dirty ? "Up to date" : <><b>{diffText(diff)}</b>{others.length ? ` · ${others.join(", ")} changed` : ""}</>}
        </span>
        <div className={s.acts}>
          {!isNew && !readOnly && actor.has("roles.roles_delete") ? <Button onClick={() => setDel(true)}>Delete role</Button> : null}
          <Button disabled={!dirty || save.isPending} onClick={() => { setDraft(saved); setSkipped(initialSkipped); setErr({ fields: {}, form: null }); }}>{t.discard}</Button>
          <Button variant="primary" type="submit" busy={save.isPending && isNew} busyLabel={t.saving} disabled={(!dirty && !isNew) || readOnly}>{t.save}</Button>
        </div>
      </div>

      <Sheet open={confirm} onClose={() => setConfirm(false)} title="Save these changes?" description={role ? `This changes what ${role.name} can do right away.` : undefined}
        footer={<div className={rs.confirmFoot}><Button onClick={() => setConfirm(false)}>{t.cancel}</Button><Button variant="primary" busy={save.isPending} busyLabel={t.saving} onClick={() => save.mutate()}>Confirm and save</Button></div>}>
        <div className={rs.confirm}>
          <p className={rs.changeText}><b>{diffText(diff)}</b></p>
          {diff.added.length ? <div><h3>Adding</h3><ul aria-label="Levers being added">{lines(diff.added).map((l) => <li key={l.id} className={rs.add}><span>{l.label}</span><small>{l.group}</small></li>)}</ul></div> : null}
          {diff.removed.length ? <div><h3>Removing</h3><ul aria-label="Levers being removed">{lines(diff.removed).map((l) => <li key={l.id} className={rs.rem}><span>{l.label}</span><small>{l.group}</small></li>)}</ul></div> : null}
          {others.length ? <p className={rs.changeText}>Also changed: {others.join(", ")}.</p> : null}
          {noChange && !others.length ? <p className={rs.changeText}>Nothing to save.</p> : null}
        </div>
      </Sheet>
      <Sheet open={del} onClose={() => { setDel(false); setDelErr(null); }} title="Delete role" description={`${role?.name ?? ""} is removed. A role that staff still use cannot be deleted.`}
        footer={<div className={rs.confirmFoot}><Button onClick={() => { setDel(false); setDelErr(null); }}>{t.cancel}</Button><Button variant="primary" busy={remove.isPending} busyLabel="Deleting…" onClick={() => remove.mutate()}>Delete role</Button></div>}>
        {delErr ? <div className={s.formErr} role="alert" style={{ margin: 0 }}>{delErr}</div> : <p className={rs.changeText}>This cannot be undone.</p>}
      </Sheet>
    </form>
  );
}
